import { getSupabaseClient } from '../lib/supabase'
import type { BookingDetails, BookingTokenResponse, CancelBookingResponse, RescheduleBookingResponse, TimeslotAvailability } from '../types/booking'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY
const FUNCTIONS_BASE_URL = SUPABASE_URL ? `${SUPABASE_URL.replace(/\/+$/, '')}/functions/v1` : ''

const defaultHeaders: HeadersInit = {
  'Content-Type': 'application/json',
}

if (SUPABASE_ANON_KEY) {
  defaultHeaders['apikey'] = SUPABASE_ANON_KEY
  defaultHeaders['Authorization'] = `Bearer ${SUPABASE_ANON_KEY}`
}

/**
 * Normalises the raw booking payload returned by Supabase into the internal BookingDetails type.
 *
 * @param payload - Raw booking data returned from the Supabase Edge Function.
 * @returns Structured and typed booking details.
 */
const normalizeBooking = (payload: any): BookingDetails => {
  return {
    id: String(payload?.id ?? payload?.booking_id ?? ''),
    customerName: String(payload?.customer_name ?? payload?.customer?.name ?? payload?.name ?? 'Guest'),
    customerEmail: payload?.customer_email ?? payload?.customer?.email ?? undefined,
    customerPhone: payload?.customer_phone ?? payload?.customer?.phone ?? undefined,
    guestCount: Number(payload?.guest_count ?? payload?.guests ?? 1),
    startTime: payload?.start_time ?? payload?.startTime ?? payload?.booking_time ?? '',
    endTime: payload?.end_time ?? payload?.endTime ?? undefined,
    timeslotId: payload?.timeslot_id ?? payload?.timeslotId ?? undefined,
    rescheduleCount: Number(payload?.reschedule_count ?? payload?.reschedules ?? 0),
    status: (payload?.status ?? 'confirmed') as BookingDetails['status'],
    locationName: payload?.location_name ?? payload?.location ?? undefined,
    notes: payload?.notes ?? undefined,
  }
}

/**
 * Normalises raw timeslot payloads into TimeslotAvailability objects.
 *
 * @param slot - Raw slot data from Supabase Edge Function.
 * @returns TimeslotAvailability or null when data is incomplete.
 */
const normalizeTimeslot = (slot: any): TimeslotAvailability | null => {
  if (!slot) return null

  const startTime = slot.startTime ?? slot.start_time ?? ''
  const endTime = slot.endTime ?? slot.end_time ?? ''
  if (!startTime || !endTime) return null

  const capacity = Number(slot.capacity ?? slot.available_capacity ?? slot.max_capacity ?? slot.limit ?? 5)
  const remaining = Math.max(
    Number(
      slot.remainingSpots ??
        slot.remaining_spots ??
        slot.available_capacity ??
        slot.remaining ??
        slot.available ??
        capacity,
    ) || 0,
    0,
  )

  const id = typeof slot.id === 'string' ? slot.id : slot.id != null ? String(slot.id) : ''
  if (!id) return null

  return {
    id,
    startTime,
    endTime,
    capacity: capacity || remaining || 5,
    remainingSpots: remaining || capacity || 5,
  }
}

/**
 * Retrieves booking details by cancellation token.
 *
 * @param token - Unique cancellation token tied to the booking.
 * @returns Booking details and booking state flags.
 */
export const getBookingByToken = async (token: string): Promise<BookingTokenResponse> => {
  if (!token) {
    throw new Error('Missing cancellation token.')
  }

  if (!FUNCTIONS_BASE_URL) {
    throw new Error('Supabase URL is not configured.')
  }

  const response = await fetch(`${FUNCTIONS_BASE_URL}/get-booking-by-token?token=${encodeURIComponent(token)}`, {
    method: 'GET',
    headers: defaultHeaders,
  })

  if (!response.ok) {
    throw new Error('Unable to retrieve booking details. Please try again.')
  }

  const data = await response.json()
  const bookingPayload = (data?.booking ?? data?.data ?? data) as any

  if (!bookingPayload) {
    throw new Error('Booking not found.')
  }

  return {
    booking: normalizeBooking(bookingPayload),
    canCancel: Boolean(data?.can_cancel ?? data?.canCancel ?? bookingPayload?.can_cancel ?? bookingPayload?.canCancel),
    canReschedule: Boolean(
      data?.can_reschedule ?? data?.canReschedule ?? bookingPayload?.can_reschedule ?? bookingPayload?.canReschedule,
    ),
    refundAmount: Number(data?.refund_amount ?? data?.refundAmount ?? bookingPayload?.refund_amount ?? 0),
    refundCurrency: data?.refund_currency ?? data?.refundCurrency ?? bookingPayload?.refund_currency ?? 'HUF',
    message: data?.message ?? undefined,
  }
}

/**
 * Cancels a booking via Supabase Edge Function.
 *
 * @param bookingId - Booking identifier.
 * @param token - Cancellation token.
 * @returns Cancellation response payload.
 */
export const cancelBooking = async (bookingId: string, token: string): Promise<CancelBookingResponse> => {
  if (!FUNCTIONS_BASE_URL) {
    throw new Error('Supabase URL is not configured.')
  }

  const response = await fetch(`${FUNCTIONS_BASE_URL}/cancel-booking`, {
    method: 'POST',
    headers: defaultHeaders,
    body: JSON.stringify({
      booking_id: bookingId,
      cancel_token: token,
    }),
  })

  if (!response.ok) {
    throw new Error('We could not cancel this booking right now. Please try again.')
  }

  const data = (await response.json()) as CancelBookingResponse
  if (!data.success) {
    throw new Error(data.message ?? 'Cancellation was unsuccessful.')
  }

  return data
}

/**
 * Reschedules a booking to a new timeslot.
 *
 * @param bookingId - Booking identifier.
 * @param token - Cancellation token.
 * @param newTimeslotId - Identifier of the new timeslot.
 * @returns Reschedule response payload.
 */
export const rescheduleBooking = async (
  bookingId: string,
  token: string,
  newTimeslotId: string,
): Promise<RescheduleBookingResponse> => {
  if (!FUNCTIONS_BASE_URL) {
    throw new Error('Supabase URL is not configured.')
  }

  const response = await fetch(`${FUNCTIONS_BASE_URL}/reschedule-booking`, {
    method: 'POST',
    headers: defaultHeaders,
    body: JSON.stringify({
      booking_id: bookingId,
      cancel_token: token,
      new_timeslot_id: newTimeslotId,
    }),
  })

  if (!response.ok) {
    throw new Error('We could not reschedule this booking right now. Please try again.')
  }

  const data = (await response.json()) as RescheduleBookingResponse
  if (!data.success) {
    throw new Error(data.message ?? 'Reschedule was unsuccessful.')
  }

  return data
}

/**
 * Retrieves available timeslots for a specific date.
 *
 * @param date - ISO date string (yyyy-MM-dd).
 * @returns Normalised list of available timeslots.
 */
export const getAvailableTimeslots = async (date: string): Promise<TimeslotAvailability[]> => {
  const supabase = getSupabaseClient()
  const { data, error } = await supabase.functions.invoke('get-available-timeslots', {
    body: { date },
  })

  if (error) {
    throw error
  }

  const raw = Array.isArray(data)
    ? data
    : Array.isArray((data as Record<string, any>)?.available_slots)
      ? ((data as Record<string, any>).available_slots as any[])
      : []

  return raw.map(normalizeTimeslot).filter((slot): slot is TimeslotAvailability => Boolean(slot))
}


